import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import Logo from '../../components/Logo/Logo';
import GoogleSignIn from '../../components/GoogleSignIn/GoogleSignIn';
import toast from 'react-hot-toast';
import './Auth.css';

const RECAPTCHA_SITE_KEY =
  // If you see "ERROR for site owner: Invalid key type",
  // it usually means you're using a reCAPTCHA v3 key with the v2 widget.
  // For local/dev, Google's v2 test key always works.
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Wait for reCAPTCHA to be ready
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.execute) {
        resolve();
      } else {
        const checkInterval = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.execute) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve();
        }, 5000);
      }
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGoogleSuccess = async (credential) => {
    try {
      setLoading(true);
      const response = await authAPI.googleAuth(credential);
      
      if (response.data && response.data.user && response.data.token) {
        login(response.data.user, response.data.token);
        toast.success('¡Bienvenido!');
        
        // Check if profile completion is needed
        if (response.data.needsCompletion) {
          navigate('/complete-profile');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Google login error:', error);
      
      // Handle email verification required
      if (error.response?.data?.emailVerificationRequired) {
        toast.error('Por favor verifica tu correo electrónico primero');
        return;
      }
      
      toast.error(error.response?.data?.message || 'Error al iniciar sesión con Google');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      setLoading(true);
      
      // Execute reCAPTCHA v3
      await waitForRecaptcha();
      if (!window.grecaptcha) {
        toast.error('Error al cargar verificación. Recarga la página.');
        setLoading(false);
        return;
      }
      
      const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'login' });
      
      const response = await authAPI.login({
        ...formData,
        recaptchaToken
      });
      
      if (response.data && response.data.user && response.data.token) {
        login(response.data.user, response.data.token);
        toast.success('¡Bienvenido de nuevo!');
        navigate('/');
      } else {
        toast.error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Check if account is deactivated
      if (error.response?.data?.deactivated) {
        const reactivate = window.confirm(
          'Tu cuenta está desactivada. ¿Deseas reactivarla ahora?'
        );
        
        if (reactivate) {
          try {
            const reactivateResponse = await authAPI.reactivateAccount({
              email: formData.email,
              password: formData.password
            });
            
            if (reactivateResponse.data) {
              login(reactivateResponse.data.user, reactivateResponse.data.token);
              toast.success('¡Cuenta reactivada exitosamente!');
              navigate('/');
              return;
            }
          } catch (reactivateError) {
            console.error('Reactivation error:', reactivateError);
            toast.error(reactivateError.response?.data?.message || 'Error al reactivar la cuenta');
          }
        }
        return;
      }
      
      toast.error(error.response?.data?.message || 'Credenciales incorrectas. Verifica tu email y contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Logo size="large" showText={true} />
            <h1 className="auth-title">Iniciar Sesión</h1>
            <p className="auth-subtitle">
              Bienvenido de nuevo, inicia sesión para continuar
            </p>
          </div>

          <GoogleSignIn onSuccess={handleGoogleSuccess} />
          
          <div className="auth-divider">
            <span>o</span>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="form-label">Correo Electrónico</label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiMail className="input-icon" />
                </span>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiLock className="input-icon" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="input-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="form-footer">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Recordarme</span>
              </label>
              <Link to="/forgot-password" className="link">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>

          <p className="auth-footer">
            ¿No tienes una cuenta?{' '}
            <Link to="/register" className="link-primary">
              Regístrate aquí
            </Link>
          </p>
        </div>

        <div className="auth-visual">
          <div className="visual-overlay"></div>
          <div className="visual-content">
            <h2>Únete a nuestra comunidad</h2>
            <p>Descubre historias fascinantes y comparte tus propias perspectivas</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
